/** RoomNexa brand accent + dark mobile auth tokens */
export const LOGIN_ACCENT = '#4ce1b6';
export const LOGIN_ACCENT_DIM = 'rgba(76, 225, 182, 0.45)';
export const LOGIN_ACCENT_GLOW = 'rgba(76, 225, 182, 0.35)';

/** Static path for auth shell logo (login, forgot password, OTP, reset). */
export const AUTH_LOGO_STATIC_PATH = 'topbar/logo_icon_bg.png';

export const mobileLoginBackground = {
  minHeight: '100vh',
  height: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(165deg, #030712 0%, #0a1a14 38%, #050505 100%)',
  backgroundColor: '#030712',
  color: '#f8fafc',
  pt: 'env(safe-area-inset-top)',
  pb: 'env(safe-area-inset-bottom)',
};

/** Shared native mobile shell surface (matches login). */
export const MOBILE_SHELL_BASE_COLOR = '#030712';
export const MOBILE_SHELL_BACKGROUND =
  'linear-gradient(165deg, #030712 0%, #0a1a14 38%, #050505 100%)';

export const mobileLoginOrb = (position, { reduceEffects = false } = {}) => ({
  position: 'absolute',
  borderRadius: '50%',
  pointerEvents: 'none',
  // Large CSS blurs are very expensive on Android WebView.
  filter: reduceEffects ? 'none' : 'blur(60px)',
  opacity: reduceEffects ? 0.35 : 1,
  ...position,
});

export const mobileGlassCard = {
  width: '100%',
  maxWidth: 420,
  mx: 'auto',
  p: { xs: 2.5, sm: 3 },
  borderRadius: '24px',
  background: 'linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
};

/** Android WebView: skip backdrop-filter (major paint cost) while keeping the same look. */
export const mobileGlassCardAndroid = {
  ...mobileGlassCard,
  background: 'linear-gradient(145deg, rgba(15,23,42,0.96) 0%, rgba(3,7,18,0.98) 100%)',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
};

/** Single-border focus — no box-shadow ring (avoids double border with global theme). */
export const authDarkTextFieldSx = {
  mb: 2.5,
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    minHeight: 52,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    color: '#f1f5f9',
    boxShadow: 'none !important',
    '& fieldset': {
      borderColor: 'rgba(148, 163, 184, 0.28)',
      borderWidth: '1px',
    },
    '&:hover:not(.Mui-disabled):not(.Mui-focused) fieldset': {
      borderColor: LOGIN_ACCENT_DIM,
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(15, 23, 42, 0.85)',
      boxShadow: 'none !important',
      '& fieldset': {
        borderColor: `${LOGIN_ACCENT} !important`,
        borderWidth: '1px !important',
      },
    },
    '&.Mui-error fieldset': {
      borderColor: '#f87171 !important',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(148, 163, 184, 0.9)',
    fontSize: '15px',
    '&.Mui-focused': {
      color: `${LOGIN_ACCENT} !important`,
      fontWeight: 500,
    },
    '&.Mui-error': {
      color: '#fca5a5',
    },
  },
  '& .MuiInputBase-input': {
    color: '#f8fafc',
    fontSize: '16px',
    padding: '14px 16px',
  },
  '& .MuiFormHelperText-root': {
    color: '#fca5a5',
    ml: 0.5,
    mt: 0.75,
  },
};

/** @deprecated use authDarkTextFieldSx via AuthDarkTextField */
export const darkFieldSx = authDarkTextFieldSx;

export const authMobileTitleSx = {
  fontWeight: 700,
  fontSize: '1.25rem',
  letterSpacing: '-0.02em',
  color: '#f8fafc',
  mb: 0.75,
};

export const authMobileSubtitleSx = {
  color: 'rgba(148, 163, 184, 0.95)',
  fontSize: '0.875rem',
  lineHeight: 1.55,
  mb: 2.5,
};

export const authMobileBackButtonSx = {
  width: 40,
  height: 40,
  color: '#e2e8f0',
  border: '1px solid rgba(255, 255, 255, 0.09)',
  backgroundColor: 'rgba(0, 0, 0, 0.58)',
  backdropFilter: 'blur(8px)',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    borderColor: LOGIN_ACCENT_DIM,
    color: LOGIN_ACCENT,
  },
  '&.Mui-disabled': {
    color: 'rgba(148, 163, 184, 0.35)',
    borderColor: 'rgba(255, 255, 255, 0.09)',
    backgroundColor: 'rgba(0, 0, 0, 0.58)',
  },
};

export const darkPrimaryButtonSx = {
  py: 1.6,
  minHeight: 52,
  borderRadius: '14px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 700,
  letterSpacing: '0.01em',
  color: '#04120e',
  background: `linear-gradient(135deg, ${LOGIN_ACCENT} 0%, #2dd4bf 55%, #14b8a6 100%)`,
  boxShadow: `0 8px 24px ${LOGIN_ACCENT_GLOW}, inset 0 1px 0 rgba(255,255,255,0.35)`,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    background: `linear-gradient(135deg, #5eead4 0%, ${LOGIN_ACCENT} 100%)`,
    boxShadow: `0 12px 28px ${LOGIN_ACCENT_GLOW}`,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    background: 'rgba(148, 163, 184, 0.25)',
    color: 'rgba(248, 250, 252, 0.5)',
    boxShadow: 'none',
  },
};

export const darkSecondaryButtonSx = {
  py: 1.5,
  minHeight: 52,
  borderRadius: '14px',
  textTransform: 'none',
  fontWeight: 600,
  color: '#e2e8f0',
  borderColor: 'rgba(148, 163, 184, 0.35)',
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
  '&:hover': {
    borderColor: LOGIN_ACCENT_DIM,
    backgroundColor: 'rgba(76, 225, 182, 0.08)',
  },
};

export const darkLinkSx = {
  color: LOGIN_ACCENT,
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 600,
  '&:hover': {
    textDecoration: 'underline',
    color: '#5eead4',
  },
};

export const darkAlertSx = {
  mb: 2,
  borderRadius: '14px',
  backgroundColor: 'rgba(239, 68, 68, 0.12)',
  border: '1px solid rgba(239, 68, 68, 0.35)',
  color: '#fecaca',
  '& .MuiAlert-icon': { color: '#f87171' },
};

export const darkInfoAlertSx = {
  mb: 2,
  borderRadius: '14px',
  backgroundColor: 'rgba(76, 225, 182, 0.1)',
  border: `1px solid ${LOGIN_ACCENT_DIM}`,
  color: '#d1fae5',
  '& .MuiAlert-icon': { color: LOGIN_ACCENT },
};

export const darkSuccessAlertSx = {
  mb: 2,
  borderRadius: '14px',
  backgroundColor: 'rgba(34, 197, 94, 0.12)',
  border: '1px solid rgba(34, 197, 94, 0.35)',
  color: '#bbf7d0',
  '& .MuiAlert-icon': { color: '#4ade80' },
};

export const authCopyrightSx = {
  color: 'rgba(100, 116, 139, 0.85)',
  fontSize: '11px',
  letterSpacing: '0.03em',
};

export const AUTH_COPYRIGHT = '© 2025 RoomNexa, Inc. Singapore · Worldwide';
